// modules/WorldNPC/WorldNPCManager.js
// NPC管理模块 - 负责NPC的创建、查找、查询等

import DebugHelper from '../DebugHelper.js';
import { GAME_CONSTANTS } from '../GameConstants.js';
import IdGenerator from '../utils/IdGenerator.js';

export default class WorldNPCManager {
    constructor(scene) {
        this.scene = scene;
        this.npcs = [];
        this.npcConfigs = null;
        this._highlightedSprite = null;
    }
    
    /**
     * 初始化NPC系统
     * @param {Array} npcConfigs - NPC配置数组
     */
    initialize(npcConfigs) {
        if (!npcConfigs || !Array.isArray(npcConfigs)) {
            DebugHelper.warn('WorldNPCManager: 没有NPC配置或配置格式错误');
            this.npcConfigs = [];
            return;
        }
        
        this.npcConfigs = npcConfigs;
        DebugHelper.log(`WorldNPCManager: 初始化完成，准备创建 ${npcConfigs.length} 个NPC`);
    }
    
    /**
     * 批量创建所有NPC
     */
    createAllNPCs() {
        if (!this.npcConfigs) return;
        
        this.npcConfigs.forEach((config, index) => {
            this.createSingleNPC(config, index);
        });
    }
    
    /**
     * 创建单个NPC
     * @param {Object} config - NPC配置
     * @param {number} index - NPC索引
     * @returns {Phaser.GameObjects.Sprite|null} 创建的NPC精灵
     */
    createSingleNPC(config, index) {
        const { name, texture, dialog = [], obstacle } = config;
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        
        const x = config.tileX * tileSize + tileSize / 2;
        const y = config.tileY * tileSize + tileSize / 2;
        
        try {
            // 1. 验证参数
            if (!texture || !this.scene.textures.exists(texture)) {
                DebugHelper.warn(`WorldNPCManager: 纹理 ${texture} 不存在`);
                return null;
            }
            
            // 2. 创建NPC精灵
            const npcSprite = this.scene.physics.add.sprite(x, y, texture);
            if (!npcSprite) {
                DebugHelper.error(`WorldNPCManager: 无法创建NPC精灵 ${name}`);
                return null;
            }
            
            // 3. 设置基本属性
            npcSprite.setOrigin(0.5);
            npcSprite.setDepth(8); // 确保NPC在背景之上
            npcSprite.setFrame(0);
            
            // 4. 设置NPC数据
            const npcId = IdGenerator.generateNPCId(index);
            const npcData = {
                id: npcId,
                name: name || '无名NPC',
                dialog: Array.isArray(dialog) ? dialog : [dialog],
                isNPC: true,
                obstacle: !!obstacle,
                config: config,
                originalPosition: { x, y },
                // 保存所有额外的配置数据（用于敌人等特殊NPC）
                isEnemy: config.isEnemy || false,
                enemyData: config.enemyData || null,
                bgm: config.bgm || null
            };

            npcSprite.setData(npcData);
            
            // 5. 设置碰撞（如果配置为障碍物）
            if (obstacle) {
                this.setupNPCCollision(npcSprite);
            }
            
            // 6. 保存到管理数组
            this.npcs.push({
                id: npcId,
                sprite: npcSprite,
                config: config,
                data: npcData
            });
            
            DebugHelper.log(`WorldNPCManager: 创建NPC ${name} 在 (${x}, ${y})`);
            return npcSprite;
            
        } catch (error) {
            DebugHelper.error(`WorldNPCManager: 创建NPC ${name} 失败:`, error);
            return null;
        }
    }
    
    /**
     * 设置NPC碰撞
     */
    setupNPCCollision(npcSprite) {
        if (!npcSprite || !npcSprite.body) return;
        
        try {
            npcSprite.setImmovable(true);
            npcSprite.setSize(28, 28);
            npcSprite.setOffset(2, 2);
            npcSprite.refreshBody();
            
            npcSprite.setData('collisionEnabled', true);
            DebugHelper.log(`WorldNPCManager: NPC ${npcSprite.getData('name')} 碰撞已启用`);
            
        } catch (error) {
            DebugHelper.error('WorldNPCManager: 设置NPC碰撞失败:', error);
        }
    }
    
    /**
     * 添加NPC到障碍组（如果需要）
     * @param {Phaser.Physics.Arcade.Group} obstacleGroup - 障碍物组
     */
    addToObstacleGroup(obstacleGroup) {
        if (!obstacleGroup || !obstacleGroup.add) {
            DebugHelper.warn('WorldNPCManager: 无效的障碍组');
            return;
        }
        
        let addedCount = 0;
        this.npcs.forEach(npc => {
            if (npc.data.obstacle && npc.sprite) {
                obstacleGroup.add(npc.sprite);
                addedCount++;
            }
        });
        
        DebugHelper.log(`WorldNPCManager: ${addedCount} 个NPC添加到障碍组`);
    }
    
    /**
     * 获取附近的NPC（用于交互）
     * @param {number} playerX - 玩家X坐标
     * @param {number} playerY - 玩家Y坐标
     * @param {number} radius - 交互半径
     * @returns {Array} 附近的NPC列表
     */
    getNearbyNPCs(playerX, playerY, radius = 50) {
        if (!this.npcs.length) return [];
        
        const nearbyNPCs = [];
        
        this.npcs.forEach(npc => {
            if (!npc.sprite || !npc.sprite.active) return;
            
            const distance = Phaser.Math.Distance.Between(
                playerX, playerY,
                npc.sprite.x, npc.sprite.y
            );
            
            if (distance <= radius) {
                nearbyNPCs.push({
                    ...npc,
                    distance: distance
                });
            }
        });
        
        return nearbyNPCs;
    }
    
    /**
     * 获取最近的可交互NPC
     * @param {number} playerX - 玩家X坐标
     * @param {number} playerY - 玩家Y坐标
     * @param {string} playerDirection - 玩家面向方向 ('up', 'down', 'left', 'right')
     * @param {number} radius - 交互半径（默认一格，32像素）
     * @returns {Object|null} 最近的可交互NPC
     */
    getNearestInteractableNPC(playerX, playerY, playerDirection, radius = 32) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        
        // 计算玩家面向方向的前一格位置
        let targetX1 = playerX;
        let targetY1 = playerY;
        
        switch (playerDirection) {
            case 'up':
                targetY1 -= tileSize;
                break;
            case 'down':
                targetY1 += tileSize;
                break;
            case 'left':
                targetX1 -= tileSize;
                break;
            case 'right':
                targetX1 += tileSize;
                break;
        }
        
        // 计算玩家面向方向的前两格位置（用于检查柜台后的NPC）
        let targetX2 = targetX1;
        let targetY2 = targetY1;
        
        switch (playerDirection) {
            case 'up':
                targetY2 -= tileSize;
                break;
            case 'down':
                targetY2 += tileSize;
                break;
            case 'left':
                targetX2 -= tileSize;
                break;
            case 'right':
                targetX2 += tileSize;
                break;
        }
        
        // 查找在交互范围内的NPC（扩大到两格范围）
        const nearbyNPCs = this.getNearbyNPCs(playerX, playerY, radius * 2);
        if (nearbyNPCs.length === 0) return null;
        
        // 过滤出符合条件的NPC
        const validNPCs = nearbyNPCs.filter(npc => {
            if (!npc.sprite || !npc.sprite.active) return false;
            
            const npcX = npc.sprite.x;
            const npcY = npc.sprite.y;
            
            // 检查NPC是否在玩家面向方向的一格内（允许一定误差）
            const distanceToTarget1 = Phaser.Math.Distance.Between(
                targetX1, targetY1,
                npcX, npcY
            );
            
            // 如果NPC在一格内，直接允许交互
            if (distanceToTarget1 <= tileSize / 2) {
                return true;
            }
            
            // 如果NPC不在一格内，检查是否在两格内
            const distanceToTarget2 = Phaser.Math.Distance.Between(
                targetX2, targetY2,
                npcX, npcY
            );
            
            // 如果NPC在两格内，检查玩家和NPC之间是否有障碍物（柜台）
            if (distanceToTarget2 <= tileSize / 2) {
                // 检查中间位置是否有障碍物
                const midTileX = Math.floor(targetX1 / tileSize);
                const midTileY = Math.floor(targetY1 / tileSize);
                
                // 如果有障碍物，允许交互（柜台后的NPC）
                if (this.isTileBlocked(midTileX, midTileY, tileSize)) {
                    return true;
                }
            }
            
            return false;
        });
        
        if (validNPCs.length === 0) {
            const fallback = nearbyNPCs.filter((npc) => !npc.data?.isEnemy);
            if (fallback.length > 0) {
                fallback.sort((a, b) => a.distance - b.distance);
                return fallback[0];
            }
            return null;
        }
        
        // 按距离排序，返回最近的
        validNPCs.sort((a, b) => a.distance - b.distance);
        return validNPCs[0];
    }

    /**
     * 高亮可交互 NPC
     * @param {Phaser.GameObjects.Sprite} sprite
     */
    setInteractableHighlight(sprite) {
        this.clearInteractableHighlight();
        if (sprite?.setTint) {
            sprite.setTint(0xaaffaa);
            this._highlightedSprite = sprite;
        }
    }

    /**
     * 清除交互高亮
     */
    clearInteractableHighlight() {
        if (this._highlightedSprite?.clearTint) {
            this._highlightedSprite.clearTint();
        }
        this._highlightedSprite = null;
    }
    
    /**
     * 检查指定格子位置是否有障碍物
     * @param {number} tileX - 格子X坐标
     * @param {number} tileY - 格子Y坐标
     * @param {number} tileSize - 格子大小
     * @param {Phaser.GameObjects.Sprite} excludeNPC - 要排除的NPC（自己）
     * @returns {boolean} 是否有障碍物
     */
    isTileBlocked(tileX, tileY, tileSize, excludeNPC = null) {
        // 获取目标位置的像素坐标
        const targetX = tileX * tileSize + tileSize / 2;
        const targetY = tileY * tileSize + tileSize / 2;
        
        // 检查是否有障碍物组
        if (this.scene.worldMap && this.scene.worldMap.obstacles) {
            const obstacles = this.scene.worldMap.obstacles;
            const checkRadius = tileSize / 2 - 2; // 检查半径，稍微小一点避免边缘碰撞
            
            // 遍历障碍物组，检查是否有障碍物在目标位置
            for (const obstacle of obstacles.children.entries) {
                if (!obstacle || !obstacle.active) continue;
                
                const distance = Phaser.Math.Distance.Between(
                    targetX, targetY,
                    obstacle.x, obstacle.y
                );
                
                // 如果距离小于检查半径，认为有障碍物
                if (distance < checkRadius) {
                    return true;
                }
            }
        }
        
        // 检查是否有其他NPC在目标位置（排除自己）
        for (const npc of this.npcs) {
            if (!npc.sprite || !npc.sprite.active) continue;
            
            // 跳过自己
            if (excludeNPC && npc.sprite === excludeNPC) continue;
            
            const walkState = npc.sprite.getData('walkState');
            if (walkState) {
                const npcTileX = walkState.currentTileX;
                const npcTileY = walkState.currentTileY;
                
                // 如果其他NPC在目标位置，认为有障碍物
                if (npcTileX === tileX && npcTileY === tileY) {
                    return true;
                }
            }
        }
        
        return false;
    }
    
    /**
     * 获取所有NPC
     * @returns {Array} 所有NPC列表
     */
    getAllNPCs() {
        return this.npcs.map(npc => npc.sprite).filter(sprite => sprite && sprite.active);
    }
    
    /**
     * 销毁所有NPC
     */
    destroy() {
        DebugHelper.log(`WorldNPCManager: 销毁 ${this.npcs.length} 个NPC`);
        
        this.npcs.forEach(npc => {
            this.cleanupNPCSprite(npc);
        });
        
        this.npcs = [];
        this.npcConfigs = null;
    }
    
    /**
     * 清理NPC精灵
     * @param {Object} npc - NPC对象
     */
    cleanupNPCSprite(npc) {
        if (npc.sprite) {
            const walkState = npc.sprite.getData('walkState');
            if (walkState && walkState.moveTween) {
                walkState.moveTween.stop();
                walkState.moveTween = null;
            }
            
            if (npc.sprite.destroy) {
                npc.sprite.destroy();
            }
        }
    }
}

